'use client';

import type { ReactElement } from 'react';
import type { FunctionReturnType } from 'convex/server';
import { MessageSquare } from 'lucide-react';

import type { api } from '@/lib/convex';
import { buildChannelMessageHref } from '@/convex/organizations/channels/channel_message_href.helpers';
import { previewChannelMessageBody } from '@/convex/organizations/channels/channel_push_policy.helpers';

export type ConnectSearchResult = FunctionReturnType<
  typeof api.organizations.channels.workspace_search.searchConnectMessages
>;
export type ConnectSearchHit = ConnectSearchResult['results'][number];

/** Where a hit opens: the message in its channel, or its thread at the reply. */
export function connectSearchHitHref(
  orgSlug: string,
  hit: ConnectSearchHit
): string {
  return hit.kind === 'reply' && hit.routeRef
    ? buildChannelMessageHref(orgSlug, {
        channelId: hit.channelId,
        routeRef: hit.routeRef,
        threadMessageId: hit.id,
      })
    : buildChannelMessageHref(orgSlug, {
        channelId: hit.channelId,
        messageId: hit.id,
      });
}

/** Plain-text snippet for a hit (mention markers reduced to `@Name`). */
export function connectSearchHitSnippet(hit: ConnectSearchHit): string {
  return previewChannelMessageBody(hit.body) ?? '';
}

export function ConnectSearchResults({
  hits,
  showChannel,
  labels,
  formatDate,
  onOpen,
}: {
  hits: ConnectSearchHit[];
  /** Workspace scope shows which chat each hit came from. */
  showChannel: boolean;
  labels: { unknown: string; threadReply: string; directMessage: string };
  formatDate: (timestamp: number) => string;
  onOpen: (hit: ConnectSearchHit) => void;
}): ReactElement {
  return (
    <ul className="space-y-2">
      {hits.map((hit) => (
        <li key={hit.id}>
          <button
            type="button"
            className="hover:bg-accent focus-visible:ring-ring w-full rounded-md border p-3 text-start focus-visible:ring-2"
            onClick={() => onOpen(hit)}
          >
            <span className="text-muted-foreground mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {showChannel ? (
                <span className="text-foreground font-medium">
                  {hit.channelLabel || labels.directMessage}
                </span>
              ) : null}
              <span>{hit.author ?? labels.unknown}</span>
              {hit.kind === 'reply' ? (
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="size-3" aria-hidden />
                  {labels.threadReply}
                </span>
              ) : null}
              <time dateTime={new Date(hit.createdAt).toISOString()}>
                {formatDate(hit.createdAt)}
              </time>
            </span>
            <span className="line-clamp-3 text-sm break-words whitespace-pre-wrap">
              {connectSearchHitSnippet(hit)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
